import { useCallback, useMemo } from 'react';
import { Shield } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { RolesModel } from '../../models/Roles';
import { RoleListRow } from './RoleListRow';

interface RolesListProps {
  roles: RolesModel[];
  fetchNextPage?: () => void;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  onEdit: (role: RolesModel) => void;
  onDelete: (role: RolesModel) => void;
}

export function RolesList({
  roles,
  fetchNextPage,
  hasNextPage = false,
  isFetchingNextPage = false,
  onEdit,
  onDelete,
}: RolesListProps) {
  const { t } = useTranslation('rolesModule');

  const rowData = useMemo(
    () =>
      roles.map((role, index) => ({
        role,
        isLast: index === roles.length - 1 && !hasNextPage,
        showActions: role.name.toLowerCase() !== 'admin',
      })),
    [roles, hasNextPage],
  );

  const handleScroll = useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      if (!hasNextPage || isFetchingNextPage || !fetchNextPage) return;

      const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
      if (scrollHeight - scrollTop - clientHeight < 120) {
        fetchNextPage();
      }
    },
    [fetchNextPage, hasNextPage, isFetchingNextPage],
  );

  return (
    <div className="flex min-h-0 flex-1 w-full flex-col overflow-hidden rounded-lg border border-border bg-background">
      <div className="grid shrink-0 grid-cols-[1fr_auto_2.5rem] items-center gap-3 border-b border-border bg-background px-4 py-3">
        <div className="flex items-center gap-2 text-sm font-medium text-foreground">
          <Shield className="h-4 w-4 shrink-0 text-icons" />
          <span>{t('list.role')}</span>
        </div>
        <span className="text-center text-xs font-medium text-muted-foreground sm:text-sm">
          {t('list.permissions')}
        </span>
        <span className="sr-only">{t('table.actions.menu')}</span>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain pb-px" onScroll={handleScroll}>
        {rowData.map(({ role, isLast, showActions }) => (
          <RoleListRow
            key={role.id}
            role={role}
            isLast={isLast}
            showActions={showActions}
            onEdit={onEdit}
            onDelete={onDelete}
          />
        ))}

        {isFetchingNextPage ? (
          <p className="px-4 py-3 text-center text-xs text-muted-foreground">{t('list.loadingMore')}</p>
        ) : null}
      </div>
    </div>
  );
}
